import { ChangeEvent, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImagePlus, X, AlertCircle, Upload } from 'lucide-react';
import { compressAndConvertToBase64, dataUrlToFile } from '@/utils/imageProcessor';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface BannerUploadProps {
  value: File | null;
  onChange: (file: File | null) => void;
  initialPreview?: string;
  onProcessed?: (base64Data: string) => void;
}

export function BannerUpload({
  value = null,
  onChange,
  initialPreview,
  onProcessed,
}: BannerUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(initialPreview || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileChange = async (file: File) => {
    setError(null);
    
    if (file) {
      // Check file size - max 10MB before compression (banners can be larger)
      if (file.size > 10 * 1024 * 1024) {
        setError('File is too large. Please select an image under 10MB.');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file.');
        return;
      }
      
      try {
        setIsProcessing(true);
        // Create a preview immediately for better UX
        const imageUrl = URL.createObjectURL(file);
        setPreview(imageUrl);
        
        // Process the image (compress and convert to base64)
        const processedImageData = await compressAndConvertToBase64(file, 1920, 800, 0.8);
        
        // Convert back to File object with JPEG format
        const processedFile = dataUrlToFile(
          processedImageData,
          `${file.name.split('.')[0]}.jpg`
        );
        
        // Call the onChange with the processed file
        onChange(processedFile);
        
        // Call onProcessed with the base64 data if provided
        if (onProcessed) {
          onProcessed(processedImageData);
        }
        
        // Update preview with processed image
        URL.revokeObjectURL(imageUrl);
        setPreview(processedImageData);
      } catch (error) {
        console.error('Error processing image:', error);
        setError('Failed to process image. Please try a different image.');
        // Don't call onChange on error
      } finally {
        setIsProcessing(false);
      }
    }
    
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleFileInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      await handleFileChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      await handleFileChange(imageFile);
    } else {
      setError('Please drop a valid image file.');
    }
  };

  const handleRemove = () => {
    onChange(null);
    setPreview(null);
    setError(null);
    if (onProcessed) {
      onProcessed('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center">
        {(value || preview) ? (
          <div className="relative w-full max-w-md">
            <div className="relative h-48 w-full rounded-lg border bg-gray-50 overflow-hidden">
              <img
                src={preview || (value ? URL.createObjectURL(value) : '')}
                alt="Banner Preview"
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={handleRemove}
                className="absolute top-2 right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`w-full max-w-md h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors ${
              isDragOver 
                ? 'border-primary bg-primary/5' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Button
              type="button"
              variant="outline"
              className="h-12 px-6 flex items-center gap-2"
              onClick={() => inputRef.current?.click()}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-600"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload Banner Image</span>
                </>
              )}
            </Button>
            <p className="text-sm text-gray-500 mt-2">
              or drag and drop an image here
            </p>
          </div>
        )}
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />
      
      <div className="text-xs text-muted-foreground space-y-1">
        <p>Recommended size: 1200x400px or similar aspect ratio (3:1).</p>
      </div>
    </div>
  );
}
